import { Scene, setLoggerProxy, setRetryCount, version } from './common/constants';
import { FSM } from 'afsm';
import EventEmitter from 'eventemitter3';
import ErrorCode from './common/error-code';
import { generateErrorMessage } from './common/error-generator';
import { ErrorInfo, ErrorName } from './common/error-info';
import RtcError from './common/rtc-error';
import {
  ILocalTrack,
  IRemoteTrack,
  IRoom,
  IRoomEventTypes,
  MediaType,
  PublishedUser,
  RemoteStreamType,
  User,
  UserRole,
  RoomOption,
  VideoCodec,
  StartMixTranscodeData, StartPublishGivenCDNData, StartPublishTencentCDNData, StopMixTranscodeData, StopPublishGivenCDNData, StopPublishTencentCDNData, SMALL_MODE
} from './enums';
import innerEmitter from './inner-emitter/inner-emitter';
import INNER_EVENT from './inner-emitter/inner-event';
import Logger from './utils/log/logger';
import { isBoolean, isNumber, isPlainObject, isString, performanceNow, stringify } from './utils/utils';
import KeyPointManager from './manager/key-point-manager';
import CallDurationCalculator from './metrics/call-duration-calculator';
import BadCaseDetector from './metrics/bad-case-detector';
import loggerManager from './utils/log/logger-manager';
import { AudioManager, BandWidthOption, rtcDetector, AbilityStatusReportData, StartPublishMixStreamToRoomData, UpdatePublishMixStreamToRoomData, StopPublishMixStreamToRoomData, LocalVideoTrack, LatencyLevel } from '.';
import { scheduleProxy, sendScheduleRequest, setScheduleProxy } from './utils/schedule';
import { VideoManager } from './manager/video-manager';
import innerKVStatManager, { KV_REPORT_KEY_NETWORK } from './manager/kv-stat-manager';

let roomSeq = 0;
export const enum RoomState {
  JOINING = 'joining',
  JOINED = 'joined',
  LEFT = 'left',
}
export abstract class BaseRoom<T extends EventEmitter.ValidEventTypes = string | symbol> extends FSM<T | IRoomEventTypes> implements IRoom {
  seq = ++roomSeq;
  sdkAppId: number;
  userId: string;
  userSig: string;
  privateMapKey?: string;
  latencyLevel?: LatencyLevel;
  tinyId?: string;
  scene: Scene;
  roomId: string;
  useStringRoomId: boolean;
  role = UserRole.ANCHOR;
  joinParams: TRTCParams | null = null;
  localPublishFlag = 0;
  localTracks = new Set<ILocalTrack>();
  // 是否开启自动播放失败时，SDK 的弹窗。
  enableAutoPlayDialog = true;
  autoReceiveAudio = true;
  autoReceiveVideo = true;
  proxy_ws?: string;
  proxy_wt?: string;
  proxy_unified?: string;
  // 默认值都为 true，进房时根据检测结果更新。
  checkSystemResult: Awaited<ReturnType<typeof rtcDetector.checkSystemRequirementsInternal>> = {
    result: true,
    detail: {
      isBrowserSupported: true,
      isWebRTCSupported: true,
      isWebCodecsSupported: true,
      isMediaDevicesSupported: true,
      isScreenShareSupported: true,
      isSmallStreamSupported: true,
      isH264EncodeSupported: true,
      isVp8EncodeSupported: true,
      isH264DecodeSupported: true,
      isVp8DecodeSupported: true,
      isH265EncodeSupported: true,
      isH265DecodeSupported: true
    }
  };
  keyPointManager: KeyPointManager;
  audioManager: AudioManager;
  videoManager: VideoManager;
  callDurationCalculator: CallDurationCalculator;
  badCaseDetector: BadCaseDetector;
  // 后台调度服务，支持信令域名调度、turn server 调度
  scheduleResult: ScheduleResult = {
    domains: null,
    iceServers: null,
    iceTransportPolicy: null,
    trtcAutoConf: null
  };
  videoDecodeFallbackType?: 'auto' | 'wasm' | 'wasm_video' | 'wasm_2d';
  smallMode: SMALL_MODE = SMALL_MODE.CANVAS;

  // 预连接状态
  preconnectPromise: Promise<void> | null = null;

  enableChorus = false;
  whipQuickStartConfig?: {
    remoteUserId: string;
    small?: boolean;
    domain?: string;
  };
  protected _isUsingCachedSchedule = false;
  abstract remotePublishedUserMap: Map<string, PublishedUser>;
  protected _log: Logger;
  // 记录进房成功时间点，在退房时统计在房时长
  protected _joinedTimestamp = 0;

  // ref: https://tapd.woa.com/20396022/prong/stories/view/1020396022115170604
  protected _sdkType?: number;
  heartbeatReport: HearBeatReport;
  heartbeatCount = 0;
  get videoCodec() {
    return VideoCodec.H264;// 默认值为 H264
  }
  get downlinkVideoCodec() {
    return VideoCodec.H264;// 默认值为 H264
  }
  get scriptTransformWorker(): Worker | undefined {
    return undefined;
  }
  /** 当前用户是否有推主流 */
  get isMainStreamPublished() {
    for (const track of this.localTracks) {
      if (track.mediaType & MediaType.BIG_VIDEO) {
        return true;
      }
    }
    return false;
  }

  /** 当前用户是否有推辅流 */
  get isAuxStreamPublished() {
    for (const track of this.localTracks) {
      if (track.mediaType & MediaType.AUX_VIDEO) {
        return true;
      }
    }
    return false;
  }

  /** 当前房间是否存在辅流，即判断本人 or 其他人是否有推辅流 */
  get hasAuxStream() {
    for (const user of this.remotePublishedUserMap.values()) {
      if (user.muteState.hasAuxiliary) {
        return true;
      }
    }
    return this.isAuxStreamPublished;
  }

  get localMainAudioTrack() {
    for (const track of this.localTracks) {
      if (track.mediaType & MediaType.AUDIO) {
        return track;
      }
    }
    return null;
  }

  get localMainVideoTrack() {
    for (const track of this.localTracks) {
      if (track.mediaType & MediaType.BIG_VIDEO) {
        return track;
      }
    }
    return null;
  }
  get localAuxVideoTrack() {
    for (const track of this.localTracks) {
      if (track.mediaType & MediaType.AUX_VIDEO) {
        return track;
      }
    }
    return null;
  }

  // 已推流状态
  get publishState() {
    const state = {
      audio: false,
      bigVideo: false,
      smallVideo: false,
      auxVideo: false
    };
    this.localTracks.forEach(track => {
      if (track.isPublished || track.isPublishing) {
        switch (track.mediaType) {
          case MediaType.AUDIO:
            state.audio = true;
            break;
          case MediaType.BIG_VIDEO:
            state.bigVideo = true;
            state.smallVideo = (track as LocalVideoTrack).hasSmall;
            break;
          case MediaType.AUX_VIDEO:
            state.auxVideo = true;
          default: break;
        }
      }
    });
    return state;
  }
  get muteState() {
    return {
      audio: !!this.localMainAudioTrack?.muted,
      bigVideo: !!this.localMainVideoTrack?.muted,
      auxVideo: !!this.localAuxVideoTrack?.muted
    };
  }

  quality?: {
    uplinkNetworkQuality: number;
    downlinkNetworkQuality: number;
    uplinkInfo: TC_UpStreamInfo;
    downlinkInfo: { rtt: number, loss: number, userId: string; }[];
    uplinkRTT: number;
    uplinkLoss: number;
    downlinkRTT: number;
    downlinkLoss: number;
  };
  // webrtc 属性
  enableSEI?: boolean;
  isDestroyed = false;

  constructor(options: RoomOption) {
    super('room');
    this._log = loggerManager.createLogger({
      parent: options.logger,
      id: `r${this.seq}`
    });
    this.useStringRoomId = !!options.useStringRoomId;
    if (isBoolean(options.autoReceiveAudio)) {
      this.autoReceiveAudio = options.autoReceiveAudio;
    }
    if (isBoolean(options.autoReceiveVideo)) {
      this.autoReceiveVideo = options.autoReceiveVideo;
    }
    // 是否开启自动播放失败时，SDK 的弹窗。
    if (isBoolean(options.enableAutoPlayDialog)) {
      this.enableAutoPlayDialog = options.enableAutoPlayDialog;
    }
    this._sdkType = options.sdkType;
    this.keyPointManager = new KeyPointManager({
      room: this,
      frameWorkType: options.frameWorkType,
      component: options.component,
      language: options.language
    });
    this.callDurationCalculator = new CallDurationCalculator({ room: this });
    this.badCaseDetector = new BadCaseDetector({ room: this });
    this.audioManager = new AudioManager(this);
    this.videoManager = new VideoManager(this);
  }

  abstract setBandWidth?(option: BandWidthOption): Promise<unknown>;

  getLogger(): Logger {
    return this._log;
  }

  abstract join(params: TRTCParams, scene: Scene, frameWorkType: number): Promise<void>;

  get isJoining() {
    return this.state.toString() === RoomState.JOINING;
  }
  get isJoined() {
    return this.state === RoomState.JOINED;
  }

  get isLeft() {
    return this.state === RoomState.LEFT;
  }

  abstract leave(): Promise<void>;

  abstract switchRole(role: UserRole): Promise<void>;

  abstract publish(localTrack: ILocalTrack): Promise<void>;

  abstract unpublish(localTrack: ILocalTrack): Promise<void>;

  abstract subscribe(...remoteTracks: IRemoteTrack[]): Promise<void>;

  abstract getSignalInfo(): SignalInfo;

  abstract unsubscribe(...remoteTracks: IRemoteTrack[]): Promise<void>;

  async addTrack(localTrack: ILocalTrack) {
    return this.publish(localTrack);
  }

  async removeTrack(localTrack: ILocalTrack) {
    return this.unpublish(localTrack);
  }

  async replaceTrack(localTrack: ILocalTrack) {
  }

  abstract changeType(isSmall: boolean, user: User): void;

  setEncodedDataProcessingListener(listener: EncodedDataProcessingListener): void {
    throw new Error('Method not implemented.');
  }

  abstract enableAudioVolumeEvaluation(interval?: number | undefined, enableInBackground?: boolean | undefined): void;

  abstract getLocalAudioStats(): Promise<LocalAudioStats>;

  abstract getLocalVideoStats(): Promise<LocalVideoStats>;

  enableAIVoice(value: boolean): void {
    throw new Error('Method not implemented.');
  }

  setProxyServer(options: string | { websocketProxy?: string | undefined; webtransportProxy?: string | undefined; loggerProxy?: string | undefined; scheduleProxy?: string | string[]; unifiedProxy?: string; }) {
    if (isString(options)) {
      if (/^wss?:\/\//i.test(options)) {
        this.proxy_ws = options;
      } else if (/^https?:\/\//i.test(options)) {
        this.proxy_wt = options;
      }
    } else if (isPlainObject(options)) {
      const { websocketProxy, webtransportProxy, loggerProxy, scheduleProxy, unifiedProxy } = options;
      this.proxy_ws = websocketProxy;
      this.proxy_wt = webtransportProxy;
      this.proxy_unified = unifiedProxy;
      if (unifiedProxy) {
        setScheduleProxy([unifiedProxy, unifiedProxy]);
        setLoggerProxy(`https://${unifiedProxy}`);
      } else {
        if (loggerProxy) setLoggerProxy(loggerProxy);
        if (scheduleProxy) setScheduleProxy(scheduleProxy);
      }
    }
    innerEmitter.once(INNER_EVENT.JOIN_RECEIVED_CMD_RES, () => this.sendAbilityStatus({
      sched_domain: scheduleProxy.main,
      sched_back_domain: scheduleProxy.backup,
      signal_domain: this.proxy_ws || this.proxy_wt || ''
    }));
  }
  abstract setTurnServer(options: TurnServerOptions | TurnServerOptions[]): void;
  async getRemoteAudioStats() {
    const statsMap: RemoteAudioStats = {};
    this.remotePublishedUserMap.forEach((user: PublishedUser) => {
      statsMap[user.userId] = user.remoteAudioTrack.stat;
    });
    return statsMap;
  }

  async getTransportStats() {
    const ts: TransportStats = { rtt: this.quality?.uplinkRTT || 0, downlinksRTT: {} };
    if (this.quality) for (const q of this.quality.downlinkInfo) {
      ts.downlinksRTT[q.userId] = q.rtt;
    }
    return ts;
  }

  async getRemoteVideoStats(type = RemoteStreamType.Main) {
    const statsMap: RemoteVideoStats = {};
    this.remotePublishedUserMap.forEach((user: PublishedUser) => {
      const rt =
        type === RemoteStreamType.Aux ? user.remoteAuxiliaryTrack : user.remoteVideoTrack;
      statsMap[user.userId] = rt.stat;
    });
    return statsMap;
  }

  checkDestroy() {
    if (this.isDestroyed) {
      throw new RtcError({
        code: ErrorCode.INVALID_OPERATION,
        message: generateErrorMessage({
          key: ErrorName.CLIENT_DESTROYED,
          data: { funName: 'join' }
        })
      });
    }
  }

  destroy() {
    if (this.isJoined) {
      this._log.warn(ErrorInfo.INVALID_DESTROY);
      throw new RtcError({
        code: ErrorCode.INVALID_OPERATION,
        message: generateErrorMessage({ key: ErrorName.INVALID_DESTROY })
      });
    }
    this._log.info('destroy room');
    this.audioManager.destroy();
    this.videoManager.destroy();
    this.keyPointManager.destroy();
    this.callDurationCalculator.destroy();
    this.badCaseDetector.destroy();
    this.isDestroyed = true;
    innerEmitter.emit(INNER_EVENT.ROOM_DESTROY, { room: this });
  }

  /**
 * 发送调度请求，包括信令调度、turn server 调度
 * @private
 * @param {String} roomId
 * @memberof BaseRoom
 */
  async schedule(param: TRTCParams, frameWorkType?: number) {
    const prevTime = performanceNow();
    try {
      const { isCached, result, detailCost } = await sendScheduleRequest({
        userId: this.userId,
        sdkAppId: this.sdkAppId,
        roomId: this.useStringRoomId ? param.strRoomId : param.roomId,
        useStringRoomId: this.useStringRoomId,
        version,
        userSig: this.userSig,
        role: this.scene === Scene.LIVE ? param.role : undefined,
        frameWorkType,
        latencyLevel: param.latencyLevel
      });

      this._isUsingCachedSchedule = isCached;

      this._log.info(`schedule cache:${+isCached} ${stringify(result, { keysToExclude: ['username', 'credential'] })}`);

      if (isCached) {
        innerEmitter.once(INNER_EVENT.JOIN_RECEIVED_CMD_RES, () => this.sendAbilityStatus({ scheduleCache: 1 }));
      }

      // @ts-ignore
      this.scheduleResult = { ...this.scheduleResult, ...result };
      if (isNumber(result.config?.retryCount)) {
        setRetryCount(result.config!.retryCount);
      }
      if (isString(result.config?.loggerDomain)) {
        setLoggerProxy(result.config!.loggerDomain);
      }
      this.videoDecodeFallbackType = result.config?.videoDecodeFallback || this.videoDecodeFallbackType;
      this.smallMode = result.config?.smallMode as unknown as SMALL_MODE || this.smallMode;
      innerEmitter.emit(INNER_EVENT.JOIN_SCHEDULE_SUCCESS, { room: this, schedule: this.scheduleResult, detailCost });
      // v5.11.0+ split 变更 50 -> 100
      innerKVStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.SCHEDULE, cost: performanceNow() - prevTime });
    } catch (error) {
      innerKVStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.SCHEDULE, error: error as RtcError });
      throw error;
    }
  }

  abstract sendStartMixTranscode(data: StartMixTranscodeData): Promise<{ data: { code: number; message: string; data: any; }; }>;
  abstract sendStopMixTranscode(data: StopMixTranscodeData): Promise<{ data: { code: number; message: string; data: any; }; }>;
  abstract sendStartPublishCDN(data: StartPublishTencentCDNData | StartPublishGivenCDNData, isTencentCDN?: boolean | undefined): Promise<{ data: { code: number; message: string; data: any; }; }>;
  abstract sendStopPublishCDN(data: StopPublishTencentCDNData | StopPublishGivenCDNData, isTencentCDN?: boolean | undefined): Promise<{ data: { code: number; message: string; data: any; }; }>;
  abstract sendStartPushStreamToRoom(data: StartPublishMixStreamToRoomData): Promise<{ data: { code: number; message: string; data: any; }; }>;
  abstract sendUpdatePushStreamToRoom(data: UpdatePublishMixStreamToRoomData): Promise<{ data: { code: number, message: string; data: any; }; }>;
  abstract sendStopPushStreamToRoom(data: StopPublishMixStreamToRoomData): Promise<{ data: { code: number, message: string; data: any; }; }>;

  sendAbilityStatus(data: AbilityStatusReportData): void {

  }

  abstract enableSmall(enable: boolean): Promise<void>;
  enableInsertableStreams() {
    return Promise.resolve();
  }
  switchRoom(options: { roomId?: number; stringRoomId?: string; userSig?: string; privateMapKey?: string; }): Promise<void> {
    return Promise.reject();
  }
  isSwitchRoomSupported() {
    return false;
  }

  abstract startUpdateNTPTime(): Promise<void> | void;
  abstract subscribeDataChannel(): Promise<void>;
  abstract unsubscribeDataChannel(): Promise<void>;

  async preconnect(sdkAppId: number, userId: string, userSig: string, frameWorkType: number, roomId?: number, strRoomId?: string): Promise<void> {
    return Promise.resolve();
  }
  async closePreconnect(): Promise<void> {
    return Promise.resolve();
  }
}
